package com.partejar.app.feature.settings.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarDockDestination
import com.partejar.app.core.ui.components.PartejarDockScaffold
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.core.security.AppLockCoordinator
import com.partejar.app.core.security.BiometricAvailability
import com.partejar.app.core.security.rememberBiometricAuthenticator
import com.partejar.app.feature.workspace.data.SessionBootstrapDto
import com.partejar.app.feature.workspace.data.SessionUser
import com.partejar.app.feature.workspace.data.UserWorkspace
import com.partejar.app.feature.workspace.data.WorkspaceProfile
import com.partejar.app.feature.workspace.presentation.WorkspaceViewModel
import com.partejar.app.feature.equipe.presentation.EquipeUiState
import com.partejar.app.feature.equipe.presentation.EquipeViewModel
import com.partejar.app.feature.entitlements.data.CommercialState
import com.partejar.app.feature.entitlements.data.WorkspaceEntitlement
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator
import org.koin.compose.koinInject
import kotlinx.coroutines.launch

@Composable
fun SettingsScreen(
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    onProfile: () -> Unit,
    onCurrentWorkspace: () -> Unit,
    onPlans: () -> Unit,
    onContractTemplates: () -> Unit,
    onCalendar: () -> Unit,
    onDeleteAccount: () -> Unit,
    onLogout: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject(),
    teamViewModel: EquipeViewModel = koinInject(),
    appLockCoordinator: AppLockCoordinator = koinInject(),
    entitlementCoordinator: EntitlementCoordinator = koinInject()
) {
    val session by viewModel.bootstrap.collectAsState()
    val selectedWorkspace = viewModel.currentWorkspace()
    val biometricAuthenticator = rememberBiometricAuthenticator()
    val biometricEnabled by appLockCoordinator.biometricEnabled.collectAsState()
    val workspaceEntitlement by entitlementCoordinator.workspace.collectAsState()
    val teamState by teamViewModel.uiState.collectAsState()
    val scope = rememberCoroutineScope()
    LaunchedEffect(selectedWorkspace?.id) {
        entitlementCoordinator.onSessionReady()
        teamViewModel.load()
    }

    SettingsLayout(
        session = session,
        currentWorkspace = selectedWorkspace,
        onProfile = onProfile,
        memberCount = (teamState as? EquipeUiState.Content)?.members?.count { it.active },
        onCurrentWorkspace = onCurrentWorkspace,
        onPlans = onPlans,
        onContractTemplates = onContractTemplates,
        onCalendar = onCalendar,
        onDeleteAccount = onDeleteAccount,
        workspaceEntitlement = workspaceEntitlement,
        biometricEnabled = biometricEnabled,
        biometricAvailable = biometricAuthenticator.availability() == BiometricAvailability.AVAILABLE,
        onBiometricClick = {
            if (biometricEnabled) appLockCoordinator.disable()
            else scope.launch { appLockCoordinator.activate(biometricAuthenticator) }
        },
        onLogout = {
            viewModel.logout()
            onLogout()
        }
    )
}

@Composable
private fun SettingsLayout(
    session: SessionBootstrapDto?,
    currentWorkspace: UserWorkspace?,
    memberCount: Int?,
    onProfile: () -> Unit,
    onCurrentWorkspace: () -> Unit,
    onPlans: () -> Unit,
    onContractTemplates: () -> Unit,
    onCalendar: () -> Unit,
    onDeleteAccount: () -> Unit = {},
    workspaceEntitlement: CommercialState<WorkspaceEntitlement>,
    biometricEnabled: Boolean,
    biometricAvailable: Boolean,
    onBiometricClick: () -> Unit,
    onLogout: () -> Unit
) {
    val spacing = PartejarTheme.spacing

    PartejarDockScaffold(
        topBar = {
            PartejarTopBar(
                title = "Configurações",
                leadingContent = {
                    Icon(
                        imageVector = PartejarIcons.Navigation.settings,
                        contentDescription = null,
                        modifier = Modifier.padding(start = spacing.space16)
                    )
                }
            )
        }
    ) { padding, dockClearance ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
            contentPadding = PaddingValues(
                start = spacing.space16,
                top = spacing.space8,
                end = spacing.space16,
                bottom = dockClearance
            ),
            verticalArrangement = Arrangement.spacedBy(spacing.space24)
        ) {
            session?.let { bootstrap ->
                item {
                    ProfileEntry(
                        user = bootstrap.usuario,
                        onClick = onProfile
                    )
                }
            }

            item {
                SettingsSection(title = "Segurança") {
                    SettingsRow(
                        title = "Desbloqueio por biometria",
                        subtitle = when {
                            biometricEnabled -> "Ativado neste dispositivo"
                            biometricAvailable -> "Use a segurança do dispositivo para abrir o Partejar"
                            else -> "Indisponível neste dispositivo"
                        },
                        icon = PartejarIcons.Navigation.profile,
                        onClick = onBiometricClick,
                        enabled = biometricEnabled || biometricAvailable
                    )
                }
            }

            item {
                SettingsSection(
                    title = "Espaço"
                ) {
                    SettingsRow(
                        title = currentWorkspace?.nome ?: "Selecionar espaço",
                        subtitle = currentWorkspace?.let { workspace ->
                            memberCount?.let { count ->
                                "${workspace.perfil.nome} · $count ${if (count == 1) "integrante" else "integrantes"}"
                            } ?: workspace.perfil.nome
                        } ?: "Escolha um espaço para continuar",
                        icon = PartejarIcons.Navigation.workspace,
                        onClick = onCurrentWorkspace
                    )
                }
            }

            if (currentWorkspace.canManageTeam()) {
                item {
                    SettingsSection(
                        title = "Administração"
                    ) {
                        SettingsRow(
                            title = "Planos e preços",
                            icon = PartejarIcons.Navigation.planos,
                            onClick = onPlans
                        )

                        SettingsDivider()

                        SettingsRow(
                            title = "Modelos de contrato",
                            icon = PartejarIcons.Contracts.template,
                            onClick = onContractTemplates
                        )
                    }
                }
            }

            item {
                SettingsSection(
                    title = "Integrações"
                ) {
                    SettingsRow(
                        title = "Calendário",
                        subtitle = "Permissões e integração com o dispositivo",
                        icon = PartejarIcons.Clinical.calendar,
                        onClick = onCalendar
                    )
                }
            }

            item {
                SettingsSection(title = "Conta") {
                    SettingsRow(
                        title = "Excluir minha conta",
                        subtitle = "Solicitar exclusão da conta profissional",
                        icon = PartejarIcons.Navigation.profile,
                        onClick = onDeleteAccount
                    )
                }
            }

            item {
                LogoutEntry(
                    onClick = onLogout
                )
            }

            item {
                PlanAndUsageSection(workspaceEntitlement)
            }
        }
    }
}

@Composable
private fun PlanAndUsageSection(state: CommercialState<WorkspaceEntitlement>) {
    SettingsSection(title = "Plano e uso") {
        when (state) {
            CommercialState.Loading -> SettingsRow(
                title = "Carregando plano do espaço",
                subtitle = "Consultando limites e recursos disponíveis",
                icon = PartejarIcons.Navigation.planos,
                onClick = {}
            )
            is CommercialState.TemporarilyUnavailable -> SettingsRow(
                title = "Plano temporariamente indisponível",
                subtitle = "Os limites não puderam ser consultados agora.",
                icon = PartejarIcons.Navigation.planos,
                onClick = {}
            )
            is CommercialState.Available -> {
                val entitlement = state.value
                SettingsRow(
                    title = "Plano ${entitlement.planCode}",
                    subtitle = "Membros ${entitlement.membersUsed.usage(entitlement.memberLimit)} · Gestantes ${entitlement.patientsUsed.usage(entitlement.patientLimit)}",
                    icon = PartejarIcons.Navigation.planos,
                    onClick = {}
                )
                SettingsDivider()
                SettingsRow(
                    title = if (entitlement.annualReportEnabled) "Relatório anual disponível" else "Relatório anual não incluso",
                    subtitle = if (entitlement.isCurrentUserOwner) "Você é a proprietária deste espaço." else "A administração comercial é feita pela proprietária.",
                    icon = PartejarIcons.Navigation.workspace,
                    onClick = {}
                )
            }
        }
    }
}

private fun Long.usage(limit: Int?): String = limit?.let { "$this/$it" } ?: "$this/ilimitado"

@Composable
private fun ProfileEntry(
    user: SessionUser,
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = PartejarShapes.radius20,
        color = MaterialTheme.colorScheme.surfaceVariant,
        contentColor = MaterialTheme.colorScheme.onSurface
    ) {
        Row(
            modifier = Modifier.padding(
                horizontal = spacing.space16,
                vertical = spacing.space12
            ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(dimensions.minimumTouchTarget),
                shape = PartejarShapes.pill,
                color = MaterialTheme.colorScheme.primaryContainer,
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer
            ) {
                Box(
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = user.nome.initials(),
                        style = MaterialTheme.typography.labelLarge
                    )
                }
            }

            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = spacing.space12)
            ) {
                Text(
                    text = user.nome,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                Text(
                    text = user.email,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Icon(
                imageVector = PartejarIcons.Actions.next,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(dimensions.iconMedium)
            )
        }
    }
}

@Composable
private fun SettingsSection(
    title: String,
    content: @Composable () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Column(
        verticalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        Text(
            text = title,
            modifier = Modifier
                .padding(horizontal = spacing.space4)
                .semantics { heading() },
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = PartejarShapes.radius14,
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface,
            border = BorderStroke(
                dimensions.strokeThin,
                MaterialTheme.colorScheme.outline
            )
        ) {
            Column {
                content()
            }
        }
    }
}

@Composable
private fun SettingsRow(
    title: String,
    icon: ImageVector,
    onClick: () -> Unit,
    subtitle: String? = null,
    enabled: Boolean = true
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = dimensions.controlHeight)
            .clickable(
                enabled = enabled,
                role = Role.Button,
                onClick = onClick
            )
            .padding(
                horizontal = spacing.space16,
                vertical = spacing.space12
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            modifier = Modifier.size(dimensions.iconLarge),
            shape = PartejarShapes.radius10,
            color = MaterialTheme.colorScheme.surfaceVariant,
            contentColor = MaterialTheme.colorScheme.primary
        ) {
            Box(
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    modifier = Modifier.size(dimensions.iconMedium)
                )
            }
        }

        Column(
            modifier = Modifier
                .weight(1f)
                .padding(horizontal = spacing.space12),
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        Icon(
            imageVector = PartejarIcons.Actions.next,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.size(dimensions.iconMedium)
        )
    }
}

@Composable
private fun SettingsDivider() {
    val spacing = PartejarTheme.spacing

    HorizontalDivider(
        modifier = Modifier.padding(start = spacing.space64),
        color = MaterialTheme.colorScheme.outlineVariant
    )
}

@Composable
private fun LogoutEntry(
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = PartejarShapes.radius14,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.error,
        border = BorderStroke(
            dimensions.strokeThin,
            MaterialTheme.colorScheme.outline
        )
    ) {
        Row(
            modifier = Modifier
                .heightIn(min = dimensions.controlHeight)
                .padding(
                    horizontal = spacing.space16,
                    vertical = spacing.space12
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(dimensions.iconLarge),
                shape = PartejarShapes.radius10,
                color = MaterialTheme.colorScheme.errorContainer,
                contentColor = MaterialTheme.colorScheme.error
            ) {
                Box(
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = PartejarIcons.Actions.logout,
                        contentDescription = null,
                        modifier = Modifier.size(dimensions.iconMedium)
                    )
                }
            }

            Text(
                text = "Sair da conta",
                modifier = Modifier.padding(start = spacing.space12),
                style = MaterialTheme.typography.bodyLarge
            )
        }
    }
}

private fun String.initials(): String {
    val parts = trim()
        .split(" ")
        .filter { it.isNotBlank() }

    return parts
        .take(2)
        .joinToString("") { part ->
            part.first().uppercaseChar().toString()
        }
}

internal fun UserWorkspace?.canManageTeam(): Boolean =
    this?.perfil?.codigo == "ADMINISTRADOR"

private val settingsPreviewSession = SessionBootstrapDto(
    usuario = SessionUser(
        1,
        "Ana Souza",
        "ana@partejar.local"
    ),
    workspaces = listOf(
        UserWorkspace(
            1,
            "Clínica Aurora",
            WorkspaceProfile(
                "ADMINISTRADOR",
                "Administrador"
            ),
            "DOULA",
            usuarioAtualEhProprietario = true
        )
    )
)

@Preview(
    name = "Configurações · Light",
    widthDp = 390,
    heightDp = 844
)
@Composable
private fun SettingsLightPreview() =
    PartejarTheme(themeMode = ThemeMode.LIGHT) {
        SettingsLayout(
            session = settingsPreviewSession,
            currentWorkspace = settingsPreviewSession.workspaces.single(),
            memberCount = 3,
            onProfile = {},
            onCurrentWorkspace = {},
            onPlans = {},
            onContractTemplates = {},
            onCalendar = {},
            workspaceEntitlement = CommercialState.Available(WorkspaceEntitlement("FREE", true, 1, 5, 3, 50, false)),
            biometricEnabled = false,
            biometricAvailable = true,
            onBiometricClick = {},
            onLogout = {}
        )
    }

@Preview(
    name = "Configurações · Dark",
    widthDp = 390,
    heightDp = 844
)
@Composable
private fun SettingsDarkPreview() =
    PartejarTheme(themeMode = ThemeMode.DARK) {
        SettingsLayout(
            session = settingsPreviewSession,
            currentWorkspace = settingsPreviewSession.workspaces.single(),
            memberCount = 3,
            onProfile = {},
            onCurrentWorkspace = {},
            onPlans = {},
            onContractTemplates = {},
            onCalendar = {},
            workspaceEntitlement = CommercialState.Available(WorkspaceEntitlement("FREE", true, 1, 5, 3, 50, false)),
            biometricEnabled = true,
            biometricAvailable = true,
            onBiometricClick = {},
            onLogout = {}
        )
    }

@Preview(
    name = "Configurações · Equipe clínica",
    widthDp = 390,
    heightDp = 844
)
@Composable
private fun SettingsClinicalPreview() =
    PartejarTheme {
        val clinicalWorkspace = settingsPreviewSession
            .workspaces
            .single()
            .copy(
                perfil = WorkspaceProfile(
                    "EQUIPE_CLINICA",
                    "Equipe clínica"
                )
            )

        SettingsLayout(
            session = settingsPreviewSession.copy(
                workspaces = listOf(clinicalWorkspace)
            ),
            currentWorkspace = clinicalWorkspace,
            memberCount = 4,
            onProfile = {},
            onCurrentWorkspace = {},
            onPlans = {},
            onContractTemplates = {},
            onCalendar = {},
            workspaceEntitlement = CommercialState.Available(WorkspaceEntitlement("ESSENCIAL", false, 2, null, 11, null, true)),
            biometricEnabled = false,
            biometricAvailable = false,
            onBiometricClick = {},
            onLogout = {}
        )
    }
